/**
 * DIEUKYDIEU.TV - STORY CONTENT & METADATA GENERATOR
 */
import { t } from '../i18n.js';

export function findArticleData(artOrTitle, artId) {
    const sitemap = (typeof window !== 'undefined' && window.SITEMAP_DATA) || [];
    
    // If passed full object with id or title or slug
    if (typeof artOrTitle === 'object' && artOrTitle !== null) {
        let matchedCat = null;
        for (const cat of sitemap) {
            if (cat.articles && cat.articles.some(a => a.id === artOrTitle.id || a.title === artOrTitle.title || (artOrTitle.slug && a.slug === artOrTitle.slug))) {
                matchedCat = cat;
                break;
            }
        }
        return { article: artOrTitle, category: matchedCat };
    }

    const searchId = typeof artOrTitle === 'number' ? artOrTitle : (artId ? Number(artId) : (!isNaN(Number(artOrTitle)) ? Number(artOrTitle) : null));
    const searchSlugOrTitle = typeof artOrTitle === 'string' ? artOrTitle.trim().toLowerCase() : '';

    for (const cat of sitemap) {
        if (!cat.articles) continue;
        for (const a of cat.articles) {
            const matchesId = (searchId !== null && (a.id === searchId || Number(a.id) === searchId));
            const matchesTitle = searchSlugOrTitle && (a.title || '').trim().toLowerCase() === searchSlugOrTitle;
            const matchesSlug = searchSlugOrTitle && (a.slug || '').trim().toLowerCase() === searchSlugOrTitle;
            if (matchesId || matchesTitle || matchesSlug) {
                return { article: a, category: cat };
            }
        }
    }

    return { article: null, category: null };
}

/**
 * Generate a realistic date string based on article id
 */
export function getArticleFormattedDate(artId) {
    const months = ['Th01', 'Th02', 'Th03', 'Th04', 'Th05', 'Th06', 'Th07', 'Th08', 'Th09', 'Th10', 'Th11', 'Th12'];
    const id = artId || 101;
    const day = ((id * 7) % 28) + 1;
    const monthIdx = (id % 12);
    const year = 2024;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    return `${formattedDay} ${months[monthIdx]}, ${year}`;
}

/**
 * Generate realistic view counts
 */
export function getArticleViews(artId) {
    const id = artId || 101;
    const views = ((id * 149) % 2800) + 1150;
    if (views >= 1000) {
        return (views / 1000).toFixed(1) + 'k lượt xem';
    }
    return `${views} lượt xem`;
}

/**
 * Generate relevant hashtags based on tag and title
 */
export function getArticleHashtags(art, category) {
    const tags = [];
    const tag = (art.tag || '').trim();
    if (tag) {
        tags.push(`#${tag.replace(/\s+/g, '')}`);
    }

    const catSlug = (category && category.slug) || '';
    if (catSlug.includes('than-ky')) {
        tags.push('#KhỏiBệnhKỳDiệu', '#PhépMàuCuộcSống', '#ThiềnĐịnh');
    } else if (catSlug.includes('phap-luan')) {
        tags.push('#ChânThiệnNhẫn', '#PhápLuânĐạiPháp', '#KhíCôngPhậtGia', '#SứcKhỏeTâmThân');
    } else if (catSlug.includes('con-nguoi')) {
        tags.push('#VẻĐẹpThuầnKhiết', '#VănHóaTruyềnThống', '#NétĐẹpNhânLoại');
    } else if (catSlug.includes('chan-tuong')) {
        tags.push('#ChânTướngSựThật', '#NhânQuyền', '#LươngTriNhânLoại');
    } else {
        tags.push('#ĐiềuKỳDiệu', '#NăngLượngTíchCực', '#TâmTínhThiệnLương');
    }

    return Array.from(new Set(tags)).slice(0, 4);
}

/**
 * Generate rich, readable article paragraphs if article has no custom content
 */
export function generateStoryContent(art, category) {
    const title = art.title || 'Câu chuyện kỳ diệu';

    if (art.id === 901 || (art.title && art.title.includes('Vì sao có nhân loại'))) {
        return `
            <div class="truth-banner banner-orange story-truth-banner">
                <div class="truth-banner-content">
                    <h4>Nhà sáng lập Pháp Luân Đại Pháp công bố bài viết:</h4>
                    <h2>VÌ SAO CÓ NHÂN LOẠI</h2>
                </div>
            </div>

            <p class="story-lead-p">
                Đầu năm 2023, nhân dịp Tết Nguyên Đán, Đại Sư Lý Hồng Chí — Nhà sáng lập Pháp Luân Công (còn gọi là Pháp Luân Đại Pháp) — đã công bố bài viết <strong>"Vì sao có nhân loại"</strong> gửi tới toàn thể công chúng khắp năm châu. Đây là lần đầu tiên Ngài trực tiếp chia sẻ cùng thế nhân về những thiên cơ bí ẩn của vũ trụ, cội nguồn sinh mệnh và nguyên nhân chân chính nhân loại tồn tại.
            </p>

            <blockquote class="story-quote-card quote-gold">
                <span class="material-symbols-rounded quote-icon">format_quote</span>
                <p>"Vũ trụ là tuần hoàn, thiên thể các giới cũng đều như vậy. Thành, Trụ, Hoại, Diệt là quy luật của vũ trụ... Mục đích làm người là để tiêu trừ tội nghiệp, tịnh hóa bản thân mà quay trở về thiên quốc."</p>
                <div class="quote-author">— Trích "Vì sao có nhân loại" - Đại Sư Lý Hồng Chí</div>
            </blockquote>

            <h3 id="sec-thien-co">Thiên Cơ Của Vũ Trụ Và Ý Nghĩa Đời Người</h3>
            <p>
                Trong bài viết, Ngài chỉ rõ vũ trụ đang ở vào thời kỳ mạt kiếp cuối cùng của giai đoạn "Diệt". Con người đến thế gian này không phải để tranh đoạt danh lợi hay hơn thua tiền tài, mà là một cơ hội vô cùng trân quý để hoàn trả nợ nghiệp, gìn giữ lòng thiện lương giữa muôn vàn mê lạc, từ đó được Thần Phật cứu độ.
            </p>
            <p>
                Bài viết là lời cảnh tỉnh từ bi vô hạn đối với nhân loại hôm nay, mang lại hy vọng tươi sáng và kim chỉ nam dẫn lối cho mọi sinh mệnh vượt qua kiếp nạn lịch sử.
            </p>

            <div class="story-insight-box insight-gold">
                <div class="insight-icon">
                    <span class="material-symbols-rounded">menu_book</span>
                </div>
                <div class="insight-content">
                    <h4>Đọc Nguyên Văn Tác Phẩm</h4>
                    <p>Mời quý độc giả đón đọc trọn vẹn bản dịch tiếng Việt chính thức của bài viết kinh điển trên các kênh truyền thông độc lập:</p>
                    <a href="https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html" target="_blank" rel="noopener noreferrer" class="story-ext-link-btn liquid-glass">
                        <span>Xem toàn văn tại DKN.TV</span>
                        <span class="material-symbols-rounded">open_in_new</span>
                    </a>
                </div>
            </div>
        `;
    }

    return `
        <p class="story-lead-p">
            Một buổi sáng mùa thu yên ả đã chứng kiến sự trở lại kỳ diệu của sự sống sau chuỗi ngày dài tưởng như tuyệt vọng. Câu chuyện không đơn thuần là một hiện tượng y học đặc biệt, mà là một minh chứng sống động cho thấy sức mạnh kỳ diệu của tinh thần, đức tin và sự thiện lương có thể vượt qua mọi giới hạn thông thường.
        </p>
        <p>
            Giữa dòng đời hối hả và nhiều áp lực, đôi khi con người cần những phút giây tĩnh lặng để nhìn sâu vào cội nguồn tâm thức. Câu chuyện <strong>"${title}"</strong> đã mở ra cánh cửa hy vọng mới cho biết bao người đang tìm kiếm hướng đi bình yên trong cuộc sống.
        </p>
        
        <blockquote class="story-quote-card">
            <span class="material-symbols-rounded quote-icon">format_quote</span>
            <p>"Khi tâm thái con người trở về với sự hòa ái, vị tha và đồng hóa với các giá trị chân chính của vũ trụ, cơ thể và hoàn cảnh xung quanh sẽ tự nhiên được điều chỉnh theo chiều hướng tốt đẹp nhất."</p>
            <div class="quote-author">— Trích dẫn chiêm nghiệm cuộc sống</div>
        </blockquote>

        <h3 id="sec-chuyen-hoa">Sự Chuyển Hóa Kỳ Diệu Từ Tâm Thức</h3>
        <p>
            Theo quan sát của các chuyên gia nghiên cứu tâm lý và y sinh học hiện đại, hệ thần kinh và hệ miễn dịch của con người có sự liên kết mật thiết với thế giới tinh thần. Khi con người buông bỏ được những oán hận, đố kỵ, lo âu và thay vào đó là sự bao dung, lương thiện, nguồn năng lượng nội sinh sẽ được kích hoạt mạnh mẽ.
        </p>
        <p>
            Trong trường hợp này, sự kiên trì thực hành và niềm tin kiên định đã mở ra một cánh cửa hy vọng mới ngay tại thời điểm dường như bế tắc nhất. Rất nhiều bạn bè, người thân đã không giấu nổi sự kinh ngạc trước những thay đổi ngoạn mục cả về thể chất lẫn sắc thái tinh thần.
        </p>

        <div class="story-insight-box">
            <div class="insight-icon">
                <span class="material-symbols-rounded">lightbulb</span>
            </div>
            <div class="insight-content">
                <h4>Bài học chiêm nghiệm</h4>
                <p>Mỗi thử thách trong cuộc đời đều ẩn chứa một cơ hội để thức tỉnh bản tính thiện lương. Giữ gìn chuẩn mực đạo đức và lòng trắc ẩn chính là chiếc chìa khóa vạn năng dẫn lối đến bình an và kỳ tích.</p>
            </div>
        </div>

        <h3 id="sec-lan-toa">Lan Tỏa Ánh Sáng Thiện Lành</h3>
        <p>
            Những câu chuyện như thế này xứng đáng được lan tỏa rộng rãi để mang lại niềm tin cho những ai đang đối mặt với khó khăn hay tìm kiếm một lối đi bình yên trong cuộc sống. Hãy cùng dieukydieu.tv chia sẻ những điều tốt đẹp này đến những người bạn yêu thương!
        </p>
    `;
}

/**
 * Format article content from markdown or plain text with paragraphs & images
 */
export function formatArticleBody(content) {
    if (!content || typeof content !== 'string') return '';
    const trimmed = content.trim();
    if (trimmed.startsWith('<p') || trimmed.startsWith('<div')) {
        return trimmed;
    }
    // Clean escaped characters like https\: or \:
    let clean = trimmed.replace(/\\([:!#[\]()])/g, '$1');

    // Split paragraphs by 2 or more newlines
    const paragraphs = clean.split(/\r?\n\s*\r?\n/);
    return paragraphs.map(p => {
        let text = p.trim();
        if (!text) return '';
        // Check for markdown image: ![alt](url)
        const imgMatch = text.match(/^!\[(.*?)\]\((.*?)\)$/);
        if (imgMatch) {
            const alt = imgMatch[1];
            const src = imgMatch[2];
            return `<figure class="story-figure"><img src="${src}" alt="${alt}" loading="lazy" />${alt ? `<figcaption>${alt}</figcaption>` : ''}</figure>`;
        }
        // Bold & italic
        text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
        // Single line breaks inside paragraph
        text = text.replace(/\r?\n/g, '<br/>');
        return `<p>${text}</p>`;
    }).filter(Boolean).join('\n');
}
